import {
	ActionButton,
	ExportDropdown,
	FilterChip,
	ListPageChartCard,
	ListPageCharts,
	ListPageLayout,
	Pagination,
	SectionCard,
	useToast,
} from '@practium/ui';
import type { SectionTab } from '@practium/ui';
import { PiePercentageChart, ComparisonLineChart } from '@practium/features/dashboard';
import { PropertiesPageSkeleton } from '../components/Skeletons/PropertiesPageSkeleton';
import { PropertiesTable } from '../components/Tables/PropertiesTable/PropertiesTable';
import { AreaTable } from '../components/Tables/AreaTable/AreaTable';
import { SectorsTable } from '../components/Tables/SectorsTable/SectorsTable';
import type { UsePropertiesPageProps } from '../hooks/usePropertiesPage';
import { usePropertiesPage } from '../hooks/usePropertiesPage';
import type { PropertiesTab } from '../types';
import { PRODUCTION_PIE_DATA, PROPERTIES_LINE_DATA } from '../constants/charts.constants';

export interface PropertiesPageProps extends UsePropertiesPageProps {
	onAddNew?: (tab: PropertiesTab) => void;
	onSelectProperty?: (id: string) => void;
	onViewItem?: (tab: PropertiesTab, id: string) => void;
	onBack?: () => void;
	onReportClick?: () => void;
}

export const PropertiesPage = ({
	onAddNew,
	onSelectProperty,
	onViewItem,
	onBack,
	onReportClick,
	...hookProps
}: PropertiesPageProps) => {
	const {
		isDetail,
		activeTab,
		onTabChange,
		selectedProperty,
		filteredProperties,
		filteredAreas,
		filteredSectors,
		searchQuery,
		setSearchQuery,
		sortAsc,
		toggleSort,
		filterValues,
		setFilterValues,
		filterGroups,
		page,
		setPage,
		totalPages,
		isLoading,
		exportProperties,
		permissions,
		showSearch,
	} = usePropertiesPage(hookProps);
	const toast = useToast();

	if (isLoading) return <PropertiesPageSkeleton />;

	const tabs: SectionTab<PropertiesTab>[] = [
		{
			key: 'properties',
			label: 'Propriedades',
			content: (
				<PropertiesTable
					properties={filteredProperties}
					onRowClick={
						isDetail
							? onViewItem
								? (id) => onViewItem('properties', id)
								: undefined
							: onSelectProperty
					}
				/>
			),
		},
	];

	if (isDetail) {
		tabs.push(
			{
				key: 'areas',
				label: 'Áreas',
				content: (
					<AreaTable
						areas={filteredAreas}
						propertyName={selectedProperty?.name}
						onRowClick={onViewItem ? (id) => onViewItem('areas', id) : undefined}
					/>
				),
			},
			{
				key: 'sectors',
				label: 'Setores',
				content: (
					<SectorsTable
						sectors={filteredSectors}
						onRowClick={onViewItem ? (id) => onViewItem('sectors', id) : undefined}
					/>
				),
			}
		);
	}

	// TODO: Connect chart reports when backend exposes their metrics.
	const handleReport = () => onReportClick?.();
	const handleExport = (format: 'pdf' | 'xlsx') => {
		void exportProperties(format).catch(() => toast.error('Não foi possível exportar propriedades.'));
	};

	const primaryAction =
		isDetail && activeTab === 'properties' ? (
			onBack && <ActionButton text="Voltar" onClick={onBack} />
		) : (
			permissions.canCreate && <ActionButton text="Adicionar" onClick={() => onAddNew?.(activeTab)} />
		);

	return (
		<ListPageLayout
			sidebarWidth="28rem"
			sidebarW1500="24rem"
			sidebarW1300="20rem"
			sidebarW1200="18rem"
			stackAt={1100}
			sidebar={
				<ListPageCharts>
					<ListPageChartCard>
						<PiePercentageChart
							title="Distribuição de Produção"
							subtitle="Distribuição da produção das suas propriedades"
							buttonText="Ver Relatório"
							onButtonClick={handleReport}
							data={PRODUCTION_PIE_DATA}
						/>
					</ListPageChartCard>
					<ListPageChartCard>
						<ComparisonLineChart
							title="Inserções na Plataforma"
							mainValue="2 Novos Setores"
							trendValue="33,3%"
							trendLabel="vs semana anterior"
							trendDirection="down"
							footerText="Inserções de 1–6 Dez, 2025"
							buttonText="Ver Relatório"
							onButtonClick={handleReport}
							data={PROPERTIES_LINE_DATA}
							xAxisKey="day"
							line1Key="atual"
							line1Label="Semana atual"
							line2Key="anterior"
							line2Label="Semana anterior"
							tooltipTitle="Inserções"
						/>
					</ListPageChartCard>
				</ListPageCharts>
			}
		>
			<SectionCard
				title="Gestão de Propriedades"
				subtitle="Gerencie propriedades, áreas e setores cadastrados no sistema"
				tabs={tabs}
				activeTab={activeTab}
				onTabChange={onTabChange}
				search={showSearch}
				searchQuery={searchQuery}
				onSearch={setSearchQuery}
				sortAsc={sortAsc}
				onToggleSort={toggleSort}
				filterSlot={
					<FilterChip
						values={filterValues}
						onChange={setFilterValues}
						groups={filterGroups}
						placeholder="Filtros"
					/>
				}
				footer={<Pagination page={page} totalPages={totalPages} onPageChange={setPage} />}
				headerAction={
					<>
						{primaryAction}
						<ExportDropdown
							onExportExcel={() => handleExport('xlsx')}
							onExportPdf={() => handleExport('pdf')}
						/>
					</>
				}
			/>
		</ListPageLayout>
	);
};
